import { apiWithBody, ModuleFE_BaseApi, ModuleFE_Toaster } from '@nu-art/thunderstorm-frontend/index';
import { ThunderDispatcherV3 } from '@nu-art/thunderstorm-frontend/core/db-api-gen/types';
import { ApiDef_PathwayTransition, DBDef_PathwayTransition, Type_ClipboardTransition, Type_ClipboardTransitionOrders } from '@app/pathway-shared';
import { ModuleFE_Expression } from '@app/hcs-frontend';
import { __stringify, exists, isValidJson, MimeType_txt, Second } from '@nu-art/ts-common';
export const dispatch_onPathwayTransitionChanged = new ThunderDispatcherV3('__onPathwayTransitionUpdated');
export class ModuleFE_PathwayTransition_Class extends ModuleFE_BaseApi {
    _v1;
    constructor() {
        super(DBDef_PathwayTransition, dispatch_onPathwayTransitionChanged);
        this._v1 = {
            updateResolutionFunction: apiWithBody(ApiDef_PathwayTransition._v1.updateResolutionFunction, async (response) => {
                // Apply the updated transitions too (they carry the new resolutionFunctionId link); otherwise
                // the transition cache stays stale and consumers gated on resolutionFunctionId (e.g. the
                // left-column resolution preview) don't reflect a newly added RF until a resync/tab switch.
                await this.onEntriesUpdated(response.transitions);
                await ModuleFE_Expression.onEntriesUpdated([response.resolutionFunction]);
                ModuleFE_Expression.IDB.setLastUpdated(response.resolutionFunction.__updated);
            }),
            updateTransitionsPriority: apiWithBody(ApiDef_PathwayTransition._v1.updateTransitionsPriority, async ({ transitions }) => {
                const latestTransitionUpdated = this.getLastUpdateTransitionTimestamp(transitions);
                await this.onEntriesUpdated(transitions);
                this.IDB.setLastUpdated(latestTransitionUpdated);
            }),
            deleteResolutionFunction: apiWithBody(ApiDef_PathwayTransition._v1.deleteResolutionFunction, async ({ transition, resolutionFunction }) => {
                await this.onEntriesUpdated([transition]);
                await ModuleFE_Expression.onEntriesDeleted([resolutionFunction]);
            }),
            copyTransition: apiWithBody(ApiDef_PathwayTransition._v1.copyTransition, async ({ pathwayTransition, resolutionFunction }) => {
                // Prime the expression cache with the cloned RF FIRST so the re-derive triggered by the transition
                // update builds the card's editable with the full RF content (terms, etc.) instead of an empty one.
                if (resolutionFunction)
                    await ModuleFE_Expression.onEntriesUpdated([resolutionFunction]);
                await this.onEntriesUpdated([pathwayTransition]);
            }),
            copyTransitionOrders: apiWithBody(ApiDef_PathwayTransition._v1.copyTransitionOrders),
            deletePathwayTransition: apiWithBody(ApiDef_PathwayTransition._v1.deletePathwayTransition, async (response) => {
                if (response.removedTransitions?.length)
                    await this.onEntriesDeleted(response.removedTransitions);
                if (response.updatedTransitions?.length)
                    await this.onEntriesUpdated(response.updatedTransitions);
                if (response.removedResolutionFunctions?.length)
                    await ModuleFE_Expression.onEntriesDeleted(response.removedResolutionFunctions);
            }),
            detachTransitionContext: apiWithBody(ApiDef_PathwayTransition._v1.detachTransitionContext, async (res) => {
                await this.onEntriesUpdated(res.pathwayTransitions.updated);
                await this.onEntriesDeleted(res.pathwayTransitions.deleted);
                await ModuleFE_Expression.onEntriesUpdated(res.resolutionFunctions.updated);
                await ModuleFE_Expression.onEntriesUpdated(res.resolutionFunctions.deleted);
            })
        };
    }
    getLastUpdateTransitionTimestamp = (transitions) => {
        return transitions.reduce((updatedTimestamp, currentTransition) => {
            if (currentTransition.__updated > updatedTimestamp)
                updatedTimestamp = currentTransition.__updated;
            return updatedTimestamp;
        }, transitions[0].__updated);
    };
    /**
     * Write a typed `{type, data}` payload to the browser clipboard as text.
     */
    writeToClipboard = async (type, data, successMessage, errorMessage) => {
        try {
            const blob = new Blob([__stringify({ type, data })], { type: MimeType_txt });
            await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
            return ModuleFE_Toaster.toastSuccess(successMessage, 3 * Second);
        }
        catch (err) {
            this.logError(err);
            return ModuleFE_Toaster.toastError(errorMessage, 3 * Second);
        }
    };
    /**
     * Copy the whole transition (transition + its orders) to the clipboard.
     */
    copyTransitionToClipboard = async (transition) => this.writeToClipboard(Type_ClipboardTransition, transition, `Transition '${transition.label ?? 'transition'}' & orders copied to clipboard`, `Could not copy transition '${transition.label ?? ''}'`);
    /**
     * Copy only the transition's orders (the four order pointer arrays) to the clipboard.
     */
    copyTransitionOrdersToClipboard = async (transition) => this.writeToClipboard(Type_ClipboardTransitionOrders, {
        _id: transition._id,
        nurseIds: transition.nurseIds,
        consultIds: transition.consultIds,
        testsIds: transition.testsIds,
        treatmentsIds: transition.treatmentsIds,
    }, `Orders from '${transition.label ?? 'transition'}' copied to clipboard`, `Could not copy orders`);
    /**
     * The copied whole-transition payload (for full-transition paste). Undefined for an orders-only clipboard.
     */
    getCopiedTransition = async () => this.readFromClipboard([Type_ClipboardTransition], this.isValidTransition);
    /**
     * The copied orders payload (for orders paste). Accepts both a full-transition and an orders-only clipboard.
     */
    getCopiedTransitionOrders = async () => this.readFromClipboard([Type_ClipboardTransition, Type_ClipboardTransitionOrders], this.isValidTransitionOrders);
    /**
     * Return the first clipboard payload whose type is allowed and whose data passes the validator.
     */
    readFromClipboard = async (allowedTypes, validator) => {
        try {
            const clipboardItems = await navigator.clipboard.read();
            for (const item of clipboardItems) {
                if (!item.types.includes(MimeType_txt))
                    continue;
                const itemContent = await (await item.getType(MimeType_txt)).text();
                if (!isValidJson(itemContent))
                    continue;
                const parsedItem = JSON.parse(itemContent);
                if (allowedTypes.includes(parsedItem.type) && validator(parsedItem.data))
                    return parsedItem;
            }
        }
        catch (err) {
            this.logError(err);
        }
    };
    isValidTransition = (data) => exists(data) && typeof data === 'object' && exists(data.sourceId) && exists(data.targetId) && Array.isArray(data.pathwayIds);
    isValidTransitionOrders = (data) => exists(data) && typeof data === 'object' &&
        (exists(data.sourceId) || [data.nurseIds, data.consultIds, data.testsIds, data.treatmentsIds].some(Array.isArray));
}
export const ModuleFE_PathwayTransition = new ModuleFE_PathwayTransition_Class();
//# sourceMappingURL=ModuleFE_PathwayTransition.js.map